import { THEME_CONFIG_KEYS } from '@lemontaps/lib/constants';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { VisibilityConfig } from '@lemontaps/lib/business-logic/features/visibility/types';
import { HidableConceptKey } from '@lemontaps/lib/business-logic/features/visibility/constants';
import { fetchThemeConfig } from '../apis/md/customization/jsonConfigs';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from '@/application/hooks';

export const initialValues = {
  hiddenConcepts: [],
  isLoading: false,
  refetch: () => {},
};

const VisibilityConfigContext = React.createContext<{
  hiddenConcepts: VisibilityConfig['hiddenConcepts'];
  isLoading: boolean;
  refetch: () => void;
}>({
  hiddenConcepts: [],
  isLoading: false,
  refetch: () => {},
});

export const VisibilityConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const domainVerified = useAppSelector(state => state.login.domainVerified);
  const { getAccessTokenSilently } = useAuth0();

  const [hiddenConcepts, setHiddenConcepts] = useState<HidableConceptKey[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { value } = await fetchThemeConfig<VisibilityConfig>(getAccessTokenSilently, {
        key: THEME_CONFIG_KEYS.VISIBILITY_CONFIG,
      });

      setHiddenConcepts(value.hiddenConcepts);
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (domainVerified) fetchConfig();
  }, [fetchConfig, domainVerified]);

  return (
    <VisibilityConfigContext.Provider
      value={{
        hiddenConcepts: hiddenConcepts || [],
        isLoading: loading,
        refetch: fetchConfig,
      }}
    >
      {children}
    </VisibilityConfigContext.Provider>
  );
};

export const useVisibility = () => {
  const context = React.useContext(VisibilityConfigContext);
  if (context === undefined) {
    throw new Error('useVisibilityConfig must be used within a VisibilityConfigProvider');
  }
  const isHidden = useCallback(
    (key: HidableConceptKey) => context.hiddenConcepts.includes(key),
    [context.hiddenConcepts],
  );
  const isVisible = useCallback(
    (key: HidableConceptKey) => !context.hiddenConcepts.includes(key),
    [context.hiddenConcepts],
  );

  const returnObj = useMemo(
    () => ({ ...context, isHidden, isVisible }),
    [context, isHidden, isVisible],
  );
  return returnObj;
};
